// ProductionNotes — the card's attributed notes thread (D-131, 2026-09-18), in the Production page
// header under the stage buttons. Hand-off notes and anyone's notes: who · when · text, newest at the
// bottom, a scrollable strip that shows the last few, an inline composer (Ctrl+Enter or Post) with a
// Hand-off toggle, and a Hand-off filter chip. Your own notes get edit / delete on hover; Admin and
// Power User can delete any. Replaces the Edit-stage "Edit notes" box (its text arrives as a note
// labelled "from Edit notes"). No notifications.
import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/api';

const ADMIN = ['Admin', 'Power User'];
function relTime(iso) {
  const t = new Date(iso).getTime(); const d = Date.now() - t;
  const m = Math.round(d / 60000); if (m < 1) return 'just now'; if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24); if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
const initialsOf = (a) => (a?.initials || (a?.name || '?').split(/\s+/).map(w => w[0]).join('').slice(0, 2)).toUpperCase();

export default function ProductionNotes({ cardId, user, compact = true }) {
  const [notes, setNotes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState('');
  const [handoff, setHandoff] = useState(false);
  const [onlyHandoff, setOnlyHandoff] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // { id, value }
  const [error, setError] = useState(null);
  const listRef = useRef(null);
  const canAdmin = (user?.roles || []).some(r => ADMIN.includes(r));
  const load = useCallback(async () => { if (!cardId) return; try { const r = await api.get(`/api/live-production/${cardId}/notes`); setNotes(r.data.notes || []); } catch { /* strip stays empty */ } finally { setLoaded(true); } }, [cardId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }, [notes.length, onlyHandoff]);
  async function post() {
    const body = draft.trim(); if (!body || busy) return;
    setBusy(true); setError(null);
    try { const r = await api.post(`/api/live-production/${cardId}/notes`, { body, isHandoff: handoff }); setNotes(n => [...n, r.data]); setDraft(''); setHandoff(false); }
    catch (e) { setError(e.response?.data?.error || 'Could not post the note'); }
    finally { setBusy(false); }
  }
  async function saveEdit() {
    if (!editing) return; const body = editing.value.trim(); const cur = notes.find(n => n.id === editing.id);
    if (!cur || body === cur.body) { setEditing(null); return; }
    try { const r = await api.patch(`/api/live-production/${cardId}/notes/${editing.id}`, { body }); setNotes(n => n.map(x => x.id === r.data.id ? r.data : x)); }
    catch (e) { setError(e.response?.data?.error || 'Could not save the note'); }
    setEditing(null);
  }
  async function toggleHandoff(note) {
    try { const r = await api.patch(`/api/live-production/${cardId}/notes/${note.id}`, { isHandoff: !note.isHandoff }); setNotes(n => n.map(x => x.id === r.data.id ? r.data : x)); }
    catch (e) { setError(e.response?.data?.error || 'Could not change the note'); }
  }
  async function remove(note) {
    if (!window.confirm('Delete this note?')) return;
    try { await api.delete(`/api/live-production/${cardId}/notes/${note.id}`); setNotes(n => n.filter(x => x.id !== note.id)); }
    catch (e) { setError(e.response?.data?.error || 'Could not delete the note'); }
  }
  const shown = onlyHandoff ? notes.filter(n => n.isHandoff) : notes;
  const handoffCount = notes.filter(n => n.isHandoff).length;
  return (
    <div className={`pn${compact ? ' compact' : ''}`} data-testid="production-notes" data-count={notes.length}>
      <div className="pn-head">
        <span className="pn-title">Notes{notes.length ? <span className="pn-count" data-testid="production-notes-count">{notes.length}</span> : null}</span>
        {handoffCount > 0 && <button type="button" className={`pn-chip${onlyHandoff ? ' on' : ''}`} data-testid="production-notes-filter" onClick={() => setOnlyHandoff(v => !v)} title={onlyHandoff ? 'Showing hand-off notes only — click for all' : 'Show hand-off notes only'}>Hand-off · {handoffCount}</button>}
      </div>
      <div className="pn-list" ref={listRef} data-testid="production-notes-list">
        {loaded && shown.length === 0 && <div className="pn-empty" data-testid="production-notes-empty">{notes.length ? 'No hand-off notes yet.' : 'No notes yet — hand-off details, reminders, anything the next person should read.'}</div>}
        {shown.map(n => {
          const mine = n.author?.id && n.author.id === user?.id;
          return (
            <div key={n.id} className={`pn-note${n.isHandoff ? ' handoff' : ''}`} data-testid="production-note" data-handoff={n.isHandoff ? 'true' : 'false'} data-mine={mine ? 'true' : 'false'}>
              <div className="pn-avatar" title={n.author?.name || 'Unattributed'}>{n.author ? initialsOf(n.author) : '—'}</div>
              <div className="pn-body">
                <div className="pn-meta">
                  <span className="pn-author" data-testid="production-note-author">{n.author?.name || 'Unattributed'}</span>
                  <span className="pn-time" title={new Date(n.createdAt).toLocaleString()}>{relTime(n.createdAt)}{n.edited ? ' · edited' : ''}</span>
                  {n.isHandoff && <span className="pn-tag" data-testid="production-note-handoff">Hand-off</span>}
                  {n.migratedFrom === 'edit_notes' && <span className="pn-tag muted" data-testid="production-note-migrated" title="Carried over from the old Edit notes box">from Edit notes</span>}
                  <span className="pn-actions">
                    {mine && editing?.id !== n.id && <button type="button" data-testid="production-note-edit" title="Edit" onClick={() => setEditing({ id: n.id, value: n.body })}>✎</button>}
                    {(mine || canAdmin) && <button type="button" data-testid="production-note-handoff-toggle" title={n.isHandoff ? 'Unmark hand-off' : 'Mark as hand-off'} onClick={() => toggleHandoff(n)}>{n.isHandoff ? '⚑' : '⚐'}</button>}
                    {(mine || canAdmin) && <button type="button" data-testid="production-note-delete" title="Delete" onClick={() => remove(n)}>✕</button>}
                  </span>
                </div>
                {editing?.id === n.id
                  ? <textarea autoFocus className="pn-edit" data-testid="production-note-edit-input" value={editing.value} onChange={e => setEditing({ id: n.id, value: e.target.value })} onBlur={saveEdit} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } if (e.key === 'Escape') setEditing(null); }} />
                  : <div className="pn-text" data-testid="production-note-text">{n.body}</div>}
              </div>
            </div>
          );
        })}
      </div>
      <div className="pn-compose">
        <textarea className="pn-input" data-testid="production-notes-input" placeholder="Add a note… (Ctrl+Enter posts)" value={draft} rows={1} onChange={e => { setDraft(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(120, e.target.scrollHeight) + 'px'; }} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); post(); } }} disabled={busy} />
        <div className="pn-compose-row">
          <label className={`pn-chip${handoff ? ' on' : ''}`} data-testid="production-notes-handoff" title="Tag this note as a hand-off for the editor"><input type="checkbox" checked={handoff} onChange={e => setHandoff(e.target.checked)} style={{ display: 'none' }} />⚑ Hand-off</label>
          {error && <span className="pn-error" data-testid="production-notes-error">{error}</span>}
          <button type="button" className="btn-primary pn-post" data-testid="production-notes-post" disabled={busy || !draft.trim()} onClick={post}>{busy ? 'Posting…' : 'Post'}</button>
        </div>
      </div>
    </div>
  );
}
